/**
 * **처음 받은 비밀번호 재설정 — 흐름·판정·문구 한 곳** (appserver 보안 원장 RT-S1 · 대응방안 결정 47·48). 의존성 없는 순수 모듈이다(Vue·Element·
 * `@/` 별칭 금지) — 화면(`components/login/PasswordResetPanel.vue`)은 이 함수들이 돌려준 상태를 그리고 효과(창 열기·요청)만 한다.
 *
 * 흐름: 로그인이 401 `{code: PASSWORD_RESET_REQUIRED, resetToken}`(또는 로그인 응답의 경고에서 「지금 바꾸기」) →
 * 1단계 사업자 정보(대표자명·상호명·사업자등록번호 → `POST …/reset/check`) →
 * 2단계 대표자 휴대폰 확인(NICE 창 — `utils/phoneVerify`·`PhoneVerifyDialog`, 사장님 비밀번호 찾기와 같은 창·같은 결과 열쇠 → 열쇠를 실어 `…/reset/check`) →
 * 3단계 새 비밀번호 두 번(`POST /admin/api/password/reset` 에 셋과 열쇠를 같이) → 새 비밀번호로 다시 로그인.
 * 서버는 단계 상태를 들고 다니지 않는다 — 앞 단계에서 통과한 값을 다음 요청마다 다시 싣는다(R-W 브리프 §1-2).
 *
 * 거절 문장은 서버 `error` 를 그대로 보인다(판정 파일 한 곳). 여기의 문장은 화면 고유의 것(단계 제목·안내·단추·아이디 안내)뿐이다.
 */
// 확장자를 적는 이유: 단위 테스트가 앱 번들 밖(node)에서 읽는다 — node ESM 은 상대 경로 확장자를 추론하지 않는다.
import { NETWORK_ERROR_MESSAGE, httpErrorBody, isTransportError, rejectionFieldsOf } from '../plugins/http/policy.js'

/** 로그인 실패 봉투의 사유 코드 — appserver `PasswordResetRequired.CODE` 와 같은 낱말 */
export const PASSWORD_RESET_REQUIRED = 'PASSWORD_RESET_REQUIRED'

/** 재설정 거절 봉투의 사유 코드 — appserver `PasswordResetRules` 와 같은 낱말(확인·재설정 두 입구가 같이 쓴다) */
export const RESET_FAILURE = Object.freeze({
  /** 재설정 토큰 만료·소진 — 처음 비밀번호로 다시 로그인해야 한다 */
  TOKEN_INVALID: 'RESET_TOKEN_INVALID',
  /** 계정에 연결된 매장·사업장이 없다 — 고객센터로 */
  BUSINESS_NOT_FOUND: 'BUSINESS_NOT_FOUND',
  /** 사업자 정보 셋 중 맞지 않는 칸이 있다(`fields`) — 고쳐 다시 넣거나 고객센터로 */
  BUSINESS_MISMATCH: 'BUSINESS_MISMATCH',
  /** 휴대폰 확인 결과가 없다(만료·다른 화면에서 받은 결과) — 휴대폰 확인을 다시 한다 */
  PHONE_NOT_VERIFIED: 'PHONE_NOT_VERIFIED',
  /** 확인한 휴대폰 명의가 등록 대표자와 다르다 — 대표자 휴대폰으로 다시 확인하거나 고객센터로 */
  NAME_MISMATCH: 'NAME_MISMATCH',
  /** 새 비밀번호가 규칙에 맞지 않는다 — 고쳐 다시 보낸다 */
  PASSWORD_INVALID: 'PASSWORD_INVALID',
})

/** 확인 입구의 성공 답 `next` — appserver 와 같은 낱말 */
export const CHECK_NEXT = Object.freeze({ PHONE: 'PHONE', PASSWORD: 'PASSWORD' })

/** 재설정 입구(로그인 전) */
export const PASSWORD_RESET_URL = '/admin/api/password/reset'

/** 확인 입구(로그인 전, 서버에 남는 것 없음) — 1단계(셋)와 2단계(셋 + 열쇠) */
export const PASSWORD_RESET_CHECK_URL = '/admin/api/password/reset/check'

/** 재설정 칸의 할 일 — 경고 대화상자도 같은 문장을 쓴다 */
const HOW_TO = '사업자 정보와 대표자 휴대폰을 확인한 뒤 새 비밀번호를 정해 주세요.'

/** 화면 문구 한 곳 — i18n 키가 한국어 문장 자체다. 점주 말로 쓴다(개발 용어 금지) */
export const PASSWORD_RESET_TEXT = Object.freeze({
  lead: `처음 받은 비밀번호를 그대로 쓰고 계십니다. ${HOW_TO}`,
  // ── 1단계 사업자 정보
  businessTitle: '1. 사업자 정보',
  businessGuide: '사업자등록증에 적힌 대표자명·상호명·사업자등록번호를 입력해 주세요.',
  ceoNm: '대표자명',
  busiNm: '상호명',
  busiNum: '사업자등록번호 (숫자 10자리)',
  businessSubmit: '사업자 정보 확인',
  businessEmpty: '대표자명·상호명·사업자등록번호를 모두 입력해 주세요.',
  // ── 2단계 대표자 휴대폰 확인
  verifyTitle: '2. 대표자 휴대폰 확인',
  verifyGuide: '대표자 본인 명의 휴대폰으로 확인해 주세요.',
  verifyButton: '휴대폰 확인',
  /** 명의가 대표자와 달랐다(NAME_MISMATCH) — 확인 단추가 이 말이 된다 */
  retryVerify: '대표자 휴대폰으로 다시 확인',
  verifyFailed: '휴대폰 확인을 마치지 못했습니다. 다시 해 주세요.',
  // ── 3단계 새 비밀번호
  passwordTitle: '3. 새 비밀번호',
  verified: '대표자 확인을 마쳤습니다. 새 비밀번호를 두 번 입력해 주세요.',
  newPassword: '새 비밀번호',
  confirmPassword: '새 비밀번호 한 번 더',
  submit: '새 비밀번호로 바꾸기',
  empty: '새 비밀번호를 입력해 주세요.',
  mismatch: '두 번 입력한 비밀번호가 다릅니다.',
  done: '새 비밀번호로 바꿨습니다. 새 비밀번호로 다시 로그인해 주세요.',
  failed: '새 비밀번호로 바꾸지 못했습니다. 잠시 후 다시 시도해 주세요.',
  back: '로그인 화면으로 돌아가기',
  // ── 고객센터(사업자 정보가 없거나 맞지 않을 때 · 명의가 다를 때)
  support: '카카오톡 고객센터',
  // ── 막기 시작 전의 경고(로그인은 된 뒤 — 결정 47 10-18 고침)
  warnTitle: '비밀번호를 바꿔 주세요',
  changeNow: '지금 바꾸기',
  later: '나중에',
  /** 경고에서 「지금 바꾸기」로 바꾼 뒤 — 이미 들어와 있으므로 다시 로그인하지 않는다 */
  doneContinue: '새 비밀번호로 바꿨습니다. 다음 로그인부터 새 비밀번호를 써 주세요.',
  /** 경고에서 바꾸다 확인 시간이 지났다 — 이미 들어와 있으므로 다음 로그인 때 다시 묻는다 */
  retryNextLogin: '확인 시간이 지나 바꾸지 못했습니다. 다음에 로그인할 때 다시 바꿔 주세요.',
})

/** 경고 대화상자에서 고른 것 */
export const DEFAULT_PASSWORD_CHOICE = Object.freeze({ NOW: 'now', LATER: 'later' })

/** 재설정 화면의 단계 — 할 일은 언제나 하나다 */
export const RESET_STEP = Object.freeze({ BUSINESS: 'business', VERIFY: 'verify', PASSWORD: 'password' })

/**
 * 1단계의 칸 — 서버 본문·거절 `fields[].field` 와 같은 이름. `digits` 가 있는 칸은 숫자만 그 자릿수까지 받는다(사업자등록번호 10자리).
 */
export const BUSINESS_FIELDS = Object.freeze([
  Object.freeze({ key: 'ceoNm', placeholder: PASSWORD_RESET_TEXT.ceoNm }),
  Object.freeze({ key: 'busiNm', placeholder: PASSWORD_RESET_TEXT.busiNm }),
  Object.freeze({ key: 'busiNum', placeholder: PASSWORD_RESET_TEXT.busiNum, digits: 10 }),
])

const BUSINESS_KEYS = BUSINESS_FIELDS.map((field) => field.key)

/** 단계마다 제목·안내 */
const STEP_VIEW = Object.freeze({
  [RESET_STEP.BUSINESS]: { title: PASSWORD_RESET_TEXT.businessTitle, guide: PASSWORD_RESET_TEXT.businessGuide },
  [RESET_STEP.VERIFY]: { title: PASSWORD_RESET_TEXT.verifyTitle, guide: PASSWORD_RESET_TEXT.verifyGuide },
  [RESET_STEP.PASSWORD]: { title: PASSWORD_RESET_TEXT.passwordTitle, guide: PASSWORD_RESET_TEXT.verified },
})

/**
 * 거절 코드 → 화면(R-W 브리프 §1-8). 표에 없는 코드(비밀번호 규칙·서버 오류)는 그 단계에 머물며 말한다.
 * `support` — 카카오톡 고객센터 단추와 아이디 안내를 보인다. `verifyButton` — 확인 단추의 말을 바꾼다.
 */
const REJECTION_SCREEN = Object.freeze({
  [RESET_FAILURE.BUSINESS_NOT_FOUND]: { step: RESET_STEP.BUSINESS, support: true },
  [RESET_FAILURE.BUSINESS_MISMATCH]: { step: RESET_STEP.BUSINESS, support: true },
  [RESET_FAILURE.PHONE_NOT_VERIFIED]: { step: RESET_STEP.VERIFY, support: false },
  [RESET_FAILURE.NAME_MISMATCH]: {
    step: RESET_STEP.VERIFY,
    support: true,
    verifyButton: PASSWORD_RESET_TEXT.retryVerify,
  },
})

/** 확인 입구의 `next` → 다음 단계 */
const CHECK_NEXT_STEP = Object.freeze({
  [CHECK_NEXT.PHONE]: RESET_STEP.VERIFY,
  [CHECK_NEXT.PASSWORD]: RESET_STEP.PASSWORD,
})

/**
 * 로그인 실패에서 **재설정 토큰** — 그 사유(401 + [PASSWORD_RESET_REQUIRED] + 토큰)가 아니면 `null`.
 *
 * @param {*} error 로그인 요청이 던진 것
 * @returns {?string}
 */
export function passwordResetTokenOf(error) {
  const body = httpErrorBody(error)
  if (!body || body.code !== PASSWORD_RESET_REQUIRED) return null

  return hasText(body.resetToken) ? body.resetToken : null
}

/**
 * 로그인 **성공** 응답의 고정값 경고 블록(`defaultPassword` — 막기 시작 전 처음 비밀번호 로그인, appserver `DefaultPasswordGate`) —
 * 시작 시각과 재설정 토큰이 다 있을 때만, 아니면 `null`.
 *
 * @returns {?{enforceFrom: string, resetToken: string}}
 */
export function defaultPasswordNoticeOf(data) {
  const block = data && typeof data === 'object' ? data.defaultPassword : null
  if (!block || !hasText(block.enforceFrom) || !hasText(block.resetToken)) return null

  return { enforceFrom: block.enforceFrom, resetToken: block.resetToken }
}

/**
 * 막기 시작 날짜의 말 — 서버가 준 한국 시각 ISO(`2026-12-01T00:00+09:00`)의 날짜 부분에서 「12월 1일」. 화면에 날짜를 박지 않는다.
 * 읽지 못하면 빈 문자열.
 */
export function enforceDateText(enforceFrom) {
  const date = /^\d{4}-(\d{2})-(\d{2})T/.exec(String(enforceFrom || ''))

  return date ? `${Number(date[1])}월 ${Number(date[2])}일` : ''
}

/** 경고 대화상자의 말 — 날짜를 읽지 못하면 그 문장만 빼고 재설정 칸의 첫 문장과 같다 */
export function defaultPasswordWarning(notice) {
  const date = enforceDateText(notice && notice.enforceFrom)
  const message = date
    ? `처음 받은 비밀번호를 그대로 쓰고 계십니다. ${date}부터는 비밀번호를 바꿔야 로그인할 수 있습니다. ${HOW_TO}`
    : PASSWORD_RESET_TEXT.lead

  return {
    title: PASSWORD_RESET_TEXT.warnTitle,
    message,
    confirmButtonText: PASSWORD_RESET_TEXT.changeNow,
    cancelButtonText: PASSWORD_RESET_TEXT.later,
  }
}

/**
 * 경고를 묻는다 — 「지금 바꾸기」면 [DEFAULT_PASSWORD_CHOICE.NOW], 「나중에」·닫기면 [DEFAULT_PASSWORD_CHOICE.LATER].
 *
 * @param {function(string, string, object): Promise} confirm 묻는 효과(Element `this.$confirm` — 확인이면 풀리고 취소·닫기면 거절)
 * @param {{enforceFrom: string, resetToken: string}} notice [defaultPasswordNoticeOf] 의 값
 * @returns {Promise<string>}
 */
export function askDefaultPasswordChange(confirm, notice) {
  const { title, message, confirmButtonText, cancelButtonText } = defaultPasswordWarning(notice)

  return Promise.resolve(confirm(message, title, { confirmButtonText, cancelButtonText, type: 'warning' })).then(
    () => DEFAULT_PASSWORD_CHOICE.NOW,
    () => DEFAULT_PASSWORD_CHOICE.LATER
  )
}

/**
 * 재설정 화면의 첫 상태 — 1단계(사업자 정보).
 *
 * - `business` — 1단계를 통과한 셋(다음 요청마다 다시 싣는다). 통과 전에는 `null`
 * - `verification` — 2단계 창이 돌려준 결과 열쇠
 * - `code` — 마지막 거절 코드(화면의 고객센터 단추·확인 단추 말이 여기서 나온다, [resetView])
 * - `fields` — 강조할 1단계 칸
 */
export function resetStart() {
  return { step: RESET_STEP.BUSINESS, business: null, verification: null, message: '', code: null, fields: [] }
}

/** 1단계 입력의 빈 값 */
export function emptyBusiness() {
  return BUSINESS_FIELDS.reduce((business, field) => Object.assign(business, { [field.key]: '' }), {})
}

/**
 * 1단계 칸에 친 값 — 숫자 칸(사업자등록번호)은 숫자만 그 자릿수까지 남긴다(하이픈을 넣어 붙여도 숫자 10자리가 된다). 새 값을 돌려준다.
 *
 * @param {object} business 지금 입력
 * @param {string} key [BUSINESS_FIELDS] 의 칸 이름
 * @param {*} value 친 값
 */
export function businessInput(business, key, value) {
  const field = BUSINESS_FIELDS.find((item) => item.key === key)
  const text = value == null ? '' : String(value)

  return { ...business, [key]: field && field.digits ? text.replace(/[^0-9]/g, '').slice(0, field.digits) : text }
}

/**
 * 1단계를 보내기 전에 화면이 볼 것 — 빈 칸만. 같은지·형식은 서버 한 곳(`PasswordResetRules`)이 답한다.
 *
 * @returns {?{message: string, fields: string[]}} 문제(없으면 `null`) — 빈 칸을 강조한다
 */
export function businessProblem(business) {
  const blank = BUSINESS_KEYS.filter((key) => !hasText(business && business[key]))

  return blank.length ? { message: PASSWORD_RESET_TEXT.businessEmpty, fields: blank } : null
}

/**
 * 확인 입구 본문 — appserver `PasswordResetController` 의 check 요청. 열쇠가 없으면(1단계) 그 칸을 싣지 않는다.
 *
 * @param {string} resetToken 로그인 관문이 준 재설정 토큰
 * @param {object} business 사업자 정보 셋
 * @param {?string} verification 2단계 창의 결과 열쇠
 */
export function checkBody(resetToken, business, verification) {
  const body = BUSINESS_KEYS.reduce((sent, key) => Object.assign(sent, { [key]: business[key] }), { resetToken })

  return hasText(verification) ? { ...body, phoneVerification: verification } : body
}

/**
 * 확인 입구가 통과시켰다 — `next` 의 단계로. 1단계 통과(`PHONE`)면 보낸 셋을 붙들고, 2단계 통과(`PASSWORD`)면 보낸 열쇠를 붙든다.
 *
 * @param {object} state 지금 상태
 * @param {object} sent 보낸 본문([checkBody])
 * @param {*} data 성공 응답 본문 `{next}`
 */
export function afterCheck(state, sent, data) {
  const step = CHECK_NEXT_STEP[data && data.next]
  if (!step) return { ...state, message: PASSWORD_RESET_TEXT.failed }

  if (step === RESET_STEP.VERIFY) {
    const business = BUSINESS_KEYS.reduce((passed, key) => Object.assign(passed, { [key]: sent[key] }), {})
    return { ...resetStart(), step, business }
  }

  return { ...state, step, verification: sent.phoneVerification, message: '', code: null, fields: [] }
}

/**
 * 휴대폰 확인 창이 끝났다 — 열쇠가 있으면 붙들고 그 열쇠로 확인 입구를 부른다(`check`), 아니면 확인 단계에 머물며 다시 하라고 말한다.
 *
 * @returns {{state: object, check: boolean}}
 */
export function afterVerify(state, outcome) {
  if (outcome && hasText(outcome.verification)) {
    return { state: { ...state, verification: outcome.verification, message: '' }, check: true }
  }

  return {
    state: { ...state, step: RESET_STEP.VERIFY, verification: null, message: PASSWORD_RESET_TEXT.verifyFailed },
    check: false,
  }
}

/**
 * 보내기 전에 화면이 볼 것 — 비었는가·두 번이 같은가만. 길이·옛 값·아이디 규칙은 서버 한 곳(`AccountPasswordPolicy.onReset`)이 답한다.
 *
 * @returns {string} 문제 문장(없으면 빈 문자열)
 */
export function newPasswordProblem(password, confirm) {
  if (!hasText(password)) return PASSWORD_RESET_TEXT.empty
  if (password !== confirm) return PASSWORD_RESET_TEXT.mismatch

  return ''
}

/** 재설정 요청 본문 — appserver `PasswordResetController.ResetRequest`: 통과한 셋 + 열쇠 + 새 비밀번호 */
export function passwordResetBody(resetToken, state, password) {
  return { ...checkBody(resetToken, state.business, state.verification), newPassword: password }
}

/**
 * 확인·재설정이 거절됐다 — 다음 할 일(코드 → 화면은 [REJECTION_SCREEN] 한 곳). `leave` 가 있으면 이 화면을 떠나 로그인으로 그 문장을 들고 간다.
 *
 * - [RESET_FAILURE.TOKEN_INVALID] → 로그인으로(처음 비밀번호로 다시 로그인해야 새 토큰이 나온다)
 * - [RESET_FAILURE.BUSINESS_NOT_FOUND]·[RESET_FAILURE.BUSINESS_MISMATCH] → 1단계로(맞지 않는 칸 강조 + 고객센터)
 * - [RESET_FAILURE.PHONE_NOT_VERIFIED] → 2단계로 / [RESET_FAILURE.NAME_MISMATCH] → 2단계로(다시 확인 + 고객센터)
 * - 그 밖(비밀번호 규칙·서버 오류·전송 오류) → 이 단계에 머물며 말한다
 *
 * 열쇠는 2단계 이전으로 돌아가는 갈래마다 버린다 — 새로 확인한 결과만 다음 요청에 싣는다.
 *
 * @returns {{state: object, leave: ?string}}
 */
export function afterSubmitFailure(state, error) {
  const body = httpErrorBody(error)
  const code = body && body.code
  const serverText = body && hasText(body.error) ? body.error.trim() : ''

  if (code === RESET_FAILURE.TOKEN_INVALID) return { state, leave: serverText || PASSWORD_RESET_TEXT.failed }

  const screen = REJECTION_SCREEN[code]
  if (screen) {
    const fields = Object.keys(rejectionFieldsOf(body))
    return {
      state: {
        ...state,
        step: screen.step,
        verification: null,
        message: serverText,
        code,
        fields: BUSINESS_KEYS.filter((key) => fields.indexOf(key) > -1),
      },
      leave: null,
    }
  }

  const message = isTransportError(error) ? NETWORK_ERROR_MESSAGE : serverText || PASSWORD_RESET_TEXT.failed

  return { state: { ...state, message }, leave: null }
}

/**
 * 화면이 그릴 것 — 단계의 제목·안내, 고객센터 단추를 보이는가, 확인 단추의 말.
 *
 * @returns {{title: string, guide: string, support: boolean, verifyButton: string}}
 */
export function resetView(state) {
  const screen = REJECTION_SCREEN[state.code] || {}

  return {
    ...STEP_VIEW[state.step],
    support: screen.support === true,
    verifyButton: screen.verifyButton || PASSWORD_RESET_TEXT.verifyButton,
  }
}

/** 고객센터 단추 밑의 말 — 카카오 채팅에는 미리 쓴 말을 넣을 수 없어 아이디를 화면에 같이 보인다 */
export function supportIdText(accountId) {
  return `문의하실 때 아이디(${accountId})를 알려 주세요.`
}

function hasText(value) {
  return typeof value === 'string' && value.trim() !== ''
}
